import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Anchoring, outside-click and scroll handling for a panel that has to escape a
 * table's overflow by living at the top of <body>.
 *
 * **The consumer renders the panel through `createPortal(…, document.body)`** and
 * puts `dropdownRef` on it. That is not a style preference: this hook used to move
 * the node itself with `document.body.appendChild(dropdownRef.current)`, which left
 * React believing the node was still a child of the JSX parent that rendered it. On
 * unmount React called `removeChild` against that parent and threw
 * `NotFoundError: The node to be removed is not a child of this node`, out of the
 * commit phase, leaving the tree inconsistent and the page needing a reload. A
 * portal is the supported way to put a node somewhere else and keep React's
 * ownership of it intact.
 *
 * The positioning below writes page coordinates (`window.scrollY` / `scrollX`), so
 * it assumes the panel is a child of <body> either way.
 *
 * Two portal shapes are both correct, for different consumers: portal ONLY
 * while open (`TableFilterModal` and friends portal unconditionally and stay
 * hidden with `hidden` instead) — the always-portalled form is fine for a
 * fixed number of panels, but anything rendered per row (one per table row,
 * e.g. `BulkApplyPanel`) must portal only while open, or it parks one hidden
 * panel in <body> per row.
 */
export default function useDropdown() {
    const triggerRef = useRef();
    const dropdownRef = useRef();
    const [isOpen, setIsOpen] = useState(false);
    const dragStartedInsideRef = useRef(false);

    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

    useEffect(() => {
        if (!isOpen) return;

        const button = triggerRef.current;
        const dropdownNode = dropdownRef.current;
        if (!button || !dropdownNode) return;

        const reposition = () => {
            const rect = button.getBoundingClientRect();

            // The trigger can live inside a horizontally scrollable table wrapper;
            // once it is scrolled out of view, close instead of floating the panel
            // over unrelated content.
            if (rect.bottom < 0 || rect.top > window.innerHeight || rect.right < 0 || rect.left > window.innerWidth) {
                setIsOpen(false);
                return;
            }

            dropdownNode.style.position = 'absolute';
            dropdownNode.style.zIndex = '9999';
            dropdownNode.style.top = rect.bottom + window.scrollY + 'px';

            // Clamp vertically so a tall panel never runs past the bottom of the viewport;
            // panels that opt into a flex column layout scroll their body within this cap
            // instead of overflowing off-screen (and pushing an Apply button below the fold).
            const bottomMargin = 12;
            dropdownNode.style.maxHeight = Math.max(200, window.innerHeight - rect.bottom - bottomMargin) + 'px';

            // Clamp horizontally so a trigger near the right edge doesn't push
            // the panel off the side of the page.
            const margin = 8;
            const maxLeft = window.scrollX + window.innerWidth - dropdownNode.offsetWidth - margin;
            dropdownNode.style.left = Math.max(window.scrollX + margin, Math.min(rect.left + window.scrollX, maxLeft)) + 'px';
        };

        reposition();
        window.addEventListener('scroll', reposition, true);
        window.addEventListener('resize', reposition);

        return () => {
            window.removeEventListener('scroll', reposition, true);
            window.removeEventListener('resize', reposition);
        };
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;

        const button = triggerRef.current;
        const dropdownNode = dropdownRef.current;
        // Should be unreachable: `isOpen && createPortal(…)` mounts the ref'd node
        // in the same commit this effect runs in, and the trigger unmounting takes
        // the whole panel — and this effect's own cleanup — with it. Guarded
        // anyway, cheaply, to match the reposition effect above: a portal-while-open
        // consumer is exactly the shape where a null ref becomes possible at all,
        // and it is now the one every new per-row consumer copies.
        if (!button || !dropdownNode) return;

        const onMouseDown = (e) => {
            if (dropdownNode.contains(e.target) || button.contains(e.target)) {
                dragStartedInsideRef.current = true;
            }
        };

        const onClickOutside = (e) => {
            if (dragStartedInsideRef.current) {
                dragStartedInsideRef.current = false;
                return;
            }
            if (!dropdownNode.contains(e.target) && !button.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', onMouseDown, true);
        document.addEventListener('click', onClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', onMouseDown, true);
            document.removeEventListener('click', onClickOutside, true);
        };
    }, [isOpen]);

    return { triggerRef, dropdownRef, isOpen, open, close, toggle };
}
